import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface UnreadCounts {
  [matchId: string]: number;
}

export const useUnreadMessages = (matchUserIds: { matchId: string; otherUserId: string }[]) => {
  const { user } = useAuthContext();
  const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({});
  const [loading, setLoading] = useState(true);

  const fetchUnreadCounts = useCallback(async () => {
    if (!user || matchUserIds.length === 0) {
      setUnreadCounts({});
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Get all chats for this user
      const { data: chats, error: chatError } = await supabase
        .from("chats")
        .select("id, user1_id, user2_id")
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`);

      if (chatError) throw chatError;

      if (!chats || chats.length === 0) {
        setUnreadCounts({});
        setLoading(false);
        return;
      }

      // Map chats to their other user
      const chatUserMap: Record<string, string> = {};
      chats.forEach((chat) => {
        const otherUserId = chat.user1_id === user.id ? chat.user2_id : chat.user1_id;
        chatUserMap[chat.id] = otherUserId;
      });

      // Get unread message counts for each chat
      const chatIds = chats.map((c) => c.id);
      const { data: messages, error: msgError } = await supabase
        .from("messages")
        .select("chat_id, sender_id, is_read")
        .in("chat_id", chatIds)
        .neq("sender_id", user.id)
        .eq("is_read", false);

      if (msgError) throw msgError;

      // Count unread per chat, then map to match
      const chatUnreadCounts: Record<string, number> = {};
      messages?.forEach((msg) => {
        chatUnreadCounts[msg.chat_id] = (chatUnreadCounts[msg.chat_id] || 0) + 1;
      });

      // Map back to matches by other user ID
      const matchCounts: UnreadCounts = {};
      matchUserIds.forEach(({ matchId, otherUserId }) => {
        // Find chat with this user
        const chatEntry = Object.entries(chatUserMap).find(([, uId]) => uId === otherUserId);
        if (chatEntry) {
          matchCounts[matchId] = chatUnreadCounts[chatEntry[0]] || 0;
        }
      });

      setUnreadCounts(matchCounts);
    } catch (error) {
      console.error("Error fetching unread counts:", error);
    } finally {
      setLoading(false);
    }
  }, [user, JSON.stringify(matchUserIds)]);

  useEffect(() => {
    fetchUnreadCounts();
  }, [fetchUnreadCounts]);

  // Subscribe to new messages
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("unread-messages-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
        },
        (payload) => {
          // Refresh counts when new message arrives
          if (payload.new && (payload.new as any).sender_id !== user.id) {
            fetchUnreadCounts();
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "messages",
        },
        () => {
          // Refresh when message is marked as read
          fetchUnreadCounts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchUnreadCounts]);

  const getUnreadCount = useCallback(
    (matchId: string) => unreadCounts[matchId] || 0,
    [unreadCounts]
  );

  const totalUnread = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);

  return {
    unreadCounts,
    getUnreadCount,
    totalUnread,
    loading,
    refetch: fetchUnreadCounts,
  };
};
